import type { DraftRowShape } from '@/lib/draft-rows/draftRow';
import { classifyAnchorDrafts } from '../utils/classifyAnchorDrafts';
import type { OrgFunctionSurfaceDrafts } from '../steps/OrgFunctionSurfaceStep';
import type { WizardDrafts } from '../useWizardSubmit';
import type { WizardProgress } from '../wizardSteps';
import {
  effectiveAnchorBaseline,
  serializeSurface,
  type WizardFlushBaselines
} from '../utils/flushBaselines';
import {
  departmentsStepSpec,
  processesStepSpec,
  systemsStepSpec,
  usersStepSpec,
  type AnchorStepSpec,
  type WizardSeeds
} from '../anchorStepSpecs';

/**
 * An anchor step is dirty when its draft classifies as a delta against its
 * flush baseline — including pending removals, which never autosave but must
 * keep the leave-page blocker armed. Shares the spec and the baseline rule
 * with the flush and the Weiter classify, so the replaceable-lane verdicts
 * stay aligned. Weiter alone widens the draft via effectiveAnchorRows
 * (adopted seeds / protected placement rows) — none of that is unsaved dirt,
 * so this check deliberately stays on the raw draft.
 */
export function isAnchorStepDirty<TData extends object, TRow extends DraftRowShape<TData>>(
  // TResponse = never: the dirty check never touches the wire, and never
  // keeps every concrete spec assignable (selectRowResults is contravariant).
  spec: AnchorStepSpec<TData, TRow, unknown, never>,
  state: {
    drafts: WizardDrafts;
    baselines: WizardFlushBaselines;
    seeds: WizardSeeds;
    progress: WizardProgress;
  }
): boolean {
  // effectiveAnchorBaseline never falls back to the seeds for an empty
  // draft, so an untouched table classifies as unchanged while a cleared
  // one (empty draft, surviving explicit baseline) reads as a pending
  // delete-all and keeps the leave-page blocker armed.
  const draftRows = spec.selectDrafts(state.drafts);
  const baseline = effectiveAnchorBaseline(
    spec.selectBaseline(state.baselines),
    spec.isDone(state.progress),
    spec.selectSeeds(state.seeds),
    draftRows
  );
  return classifyAnchorDrafts(baseline ?? [], draftRows).kind !== 'unchanged';
}

/**
 * A surface step is dirty when its snapshot differs from the last-flushed one
 * or removals are pending — removals never autosave (they commit only via
 * Weiter), so they must keep the leave-page blocker and Abschließen gate
 * armed even though no flush will pick them up.
 */
export function isSurfaceStepDirty(
  drafts: OrgFunctionSurfaceDrafts,
  baselineSnapshot: string | undefined
): boolean {
  if (drafts.removedEntityIds.length + drafts.membershipRemovals.length > 0) return true;
  if (drafts.rows.length + drafts.memberships.length === 0) return false;
  return serializeSurface(drafts) !== baselineSnapshot;
}

/** Whether any covered step's draft differs from its flush baseline. */
export function computeWizardDirty(
  drafts: WizardDrafts,
  baselines: WizardFlushBaselines,
  seeds: WizardSeeds,
  progress: WizardProgress
): boolean {
  const state = { drafts, baselines, seeds, progress };
  return (
    isAnchorStepDirty(departmentsStepSpec, state) ||
    isAnchorStepDirty(usersStepSpec, state) ||
    isSurfaceStepDirty(drafts.functionDrafts, baselines.functions) ||
    isSurfaceStepDirty(drafts.teamDrafts, baselines.teams) ||
    isAnchorStepDirty(systemsStepSpec, state) ||
    isAnchorStepDirty(processesStepSpec, state)
  );
}
